import { Stack } from '@mui/material';
import type { FieldArrayRenderProps, FormikHelpers } from 'formik';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import ContentLoader from '@components/ContentLoader';
import RoundedButton from '@components/RoundedButton/index';

import { useAccountPlans } from '@hooks/api/account';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import useQuestionnaire from '@hooks/useQuestionnaire';

import type { OnboardingMember } from '@models/onboarding-member';
import { newPhoneEntry } from '@models/patient';

import { PlanType } from 'generated/types';
import { useEmailExistsLazyQuery } from 'generated/user.generated';

import NewAccountForm from './NewAccountForm';

const messages = defineMessages({
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  add: {
    id: 'newAccountForm.add',
    defaultMessage: 'Add another user',
  },
  submit: {
    id: 'GLOBAL.next',
    defaultMessage: 'Next',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
  emailTaken: {
    id: 'newAccountForm.emailTaken',
    defaultMessage: 'Email taken',
  },
  invalidDate: {
    id: 'GLOBAL.invalidDate',
    defaultMessage: 'Invalid Date',
  },
  mustHaveMembers: {
    id: 'newAccountForm.mustHaveMembers',
    defaultMessage: 'Must have members',
  },
  minimumMembers: {
    id: 'newAccountForm.minimumMembers',
    defaultMessage: 'Minimum of {number} members',
  },
});

/**
 * Guarantees each member owns one entry array with at least one row, whatever
 * the caller passed. Callers spread a shared `defaultMember`, so building the
 * array here is what keeps two members from aliasing one.
 */
const withDefaultPhones = (members: OnboardingMember[]): OnboardingMember[] =>
  members.map((member) => ({
    ...member,
    phones: member.phones?.length ? member.phones : [newPhoneEntry()],
  }));

export const defaultMember: OnboardingMember = {
  plan: null,
  pricing: null,
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  role: null,
  isPatient: false,
  dateOfBirth: null,
  phones: [],
  planId: null,
  questionnaireResponses: undefined,
};

interface NewAccountParentProps {
  /**
   * The parent ID (organization) users are being added under.
   * If provided, a plan type dropdown will be rendered for each user and will be required.
   */
  parent_id: string;
  /**
   * If `true` fetches the plans for the `parent_id` account. `false` skips this fetch.
   */
  shouldFetchPlans?: boolean;
}

interface NewAccountFormCollectionProps {
  initialMembers: OnboardingMember[];
  onSubmit?: (values: { members: OnboardingMember[] }) => void;
  onBack?: () => void;
  maxUsers?: number;
  isAddForm?: boolean;
  /**
   * If `true`, all new members are treated as patients. If `false`, all new members are not treated as patients. Otherwise, all fields are shown.
   */
  isPatient?: boolean;
  /**
   * Properties that apply to the parent account of the new account/profile
   */
  parentProps?: NewAccountParentProps;
}

type FormValues = { members: OnboardingMember[] };

const NewAccountFormCollection = ({
  initialMembers = [],
  onSubmit,
  onBack,
  maxUsers = 0,
  isAddForm = false,
  isPatient,
  parentProps,
}: NewAccountFormCollectionProps) => {
  const { formatMessage } = useIntl();
  const { emailSchema, phoneSchema } = useCommonSchemas();
  const [emailExists] = useEmailExistsLazyQuery();

  // Different from PATIENT and CAREGIVER plans
  const {
    planLimits,
    planOptions,
    loading: accountPlansLoading,
  } = useAccountPlans(
    parentProps?.shouldFetchPlans ? parentProps.parent_id : null
  );
  const { loading: questionnaireLoading, formHelpers } = useQuestionnaire();
  const loading = accountPlansLoading || questionnaireLoading;

  const addUser = (numUsers: number, arrayHelpers: FieldArrayRenderProps) => {
    if (numUsers < maxUsers || maxUsers === 0) {
      arrayHelpers.push({ ...defaultMember, phones: [newPhoneEntry()] });
    }
  };

  const onboardingMemberSchema: Yup.Schema<OnboardingMember> =
    Yup.object().shape({
      isPatient: Yup.boolean().required(),
      role: Yup.string().nullable(),
      plan: Yup.string().oneOf([PlanType.PATIENT]).nullable(),
      pricing: Yup.string().oneOf([PlanType.PATIENT]).nullable(),
      planId: Yup.string()
        .nullable()
        .oneOf(planOptions.map(({ k }) => k)),
      firstName: Yup.string().required(formatMessage(messages.requiredText)),
      middleName: Yup.string().nullable(),
      lastName: Yup.string().required(formatMessage(messages.requiredText)),
      email: emailSchema
        // eslint-disable-next-line func-names
        .test(
          'email',
          formatMessage(messages.requiredText),
          async (email, { parent, path }) => {
            const { plan } = parent as OnboardingMember;
            const index = parseInt(path.split('[')[1].split(']')[0], 10);
            const required =
              (index === 0 && !isAddForm) ||
              plan === null ||
              (isAddForm && plan !== PlanType.PATIENT);

            return Boolean((required && email) || !required);
          }
        ),
      dateOfBirth: Yup.string()
        .typeError(formatMessage(messages.invalidDate))
        .nullable()
        .when('plan', {
          is: (plan: PlanType) => {
            return [PlanType.PATIENT].includes(plan);
          },
          then: () => {
            return Yup.string()
              .nullable()
              .required(formatMessage(messages.requiredText));
          },
        }),
      // Optional even for patients — product signed off on collecting it,
      // but didn't ask for it to block submission when left blank. Every
      // form renders one empty row, and toPhoneContactInputs drops the ones
      // still blank at submit.
      phones: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(),
          value: phoneSchema.default(''),
          use: Yup.string().oneOf(['mobile', 'home', 'work', 'fax']).required(),
          isPrimary: Yup.boolean().required(),
        })
      ),
      questionnaireResponses: Yup.object()
        .shape({
          ...formHelpers.schemaFields,
        })
        .nullable(),
    });

  // Validation schema
  const planMembersSchema: Yup.Schema<FormValues> = Yup.object().shape({
    members: Yup.array()
      .of(onboardingMemberSchema)
      .required(formatMessage(messages.mustHaveMembers))
      .min(1, formatMessage(messages.minimumMembers, { number: 1 }))
      .test('plan-limit', (users, { createError, path }) => {
        if (!users.length) return true;
        // Count new selections per plan
        const newCounts = users.reduce<Record<string, number>>((acc, user) => {
          if (!user.planId) {
            return acc;
          }
          acc[user.planId] = (acc[user.planId] || 0) + 1;
          return acc;
        }, {});

        // Check against limits + existing counts
        for (const planId of Object.keys(newCounts)) {
          const currentTotal = planLimits[planId].total || 0;
          const selectedTotal = newCounts[planId] || 0;
          const newTotal = currentTotal + selectedTotal;
          const limit = planLimits[planId].limit || 0;
          if (newTotal > limit) {
            const exceedAmount = newTotal - limit;
            const plan = planOptions.find(({ k }) => k === planId);
            return createError({
              path,
              message: `${exceedAmount} too many users selected for ${plan?.v}. Limit is ${limit}, ${currentTotal} are assigned already.`,
            });
          }
        }

        return true;
      }),
  });

  async function handleSubmit(
    values: { members: OnboardingMember[] },
    { setSubmitting, setErrors }: FormikHelpers<{ members: OnboardingMember[] }>
  ) {
    // Check emails
    const responses = await Promise.all(
      values.members.map(async ({ email }) => {
        if (email) {
          const result = await emailExists({ variables: { input: { email } } });
          return result.data?.emailExists;
        }
        return false;
      })
    );

    // Email message
    if (responses.some((resp) => resp)) {
      setErrors({
        members: responses.map((resp) => ({
          email: resp ? formatMessage(messages.emailTaken) : '',
        })),
      });

      setSubmitting(false);
    } else if (onSubmit) {
      // Map plans (only one Patient)
      let patientFound = false;
      values.members.forEach((member) => {
        if (member.pricing === PlanType.PATIENT) {
          if (patientFound) {
            member.pricing = PlanType.PATIENT;
          } else {
            patientFound = true;
          }
        }
      });

      onSubmit(values);
    } else {
      setSubmitting(false);
    }
  }

  // Return form
  return (
    <ContentLoader loading={loading}>
      <Formik
        enableReinitialize
        initialValues={{
          members: withDefaultPhones(initialMembers),
        }}
        onSubmit={handleSubmit}
        validationSchema={planMembersSchema}
      >
        {({ isSubmitting, values }) => {
          return (
            <Form>
              <FieldArray
                name="members"
                render={(arrayHelpers) => (
                  <>
                    <Stack gap={1}>
                      {values.members.map((member, index) => (
                        <NewAccountForm
                          arrayHelpers={arrayHelpers}
                          index={index}
                          isAddForm={isAddForm}
                          key={index}
                          value={member}
                          isPatient={isPatient}
                          planOptions={planOptions}
                        />
                      ))}
                    </Stack>

                    <ErrorMessage
                      name="members"
                      render={(e) => {
                        if (typeof e !== 'string') {
                          return null;
                        }
                        return <p className="error-msg">{e}</p>;
                      }}
                    />

                    {(!maxUsers || values.members.length < maxUsers) && (
                      <RoundedButton
                        onClick={() =>
                          addUser(values.members.length, arrayHelpers)
                        }
                        type="button"
                      >
                        {formatMessage(messages.add)}
                      </RoundedButton>
                    )}

                    <RoundedButton disabled={isSubmitting} type="submit">
                      {formatMessage(messages.submit)}
                    </RoundedButton>
                    {isAddForm && (
                      <RoundedButton
                        disabled={isSubmitting}
                        onClick={onBack}
                        type="button"
                      >
                        {formatMessage(messages.back)}
                      </RoundedButton>
                    )}
                  </>
                )}
              />
            </Form>
          );
        }}
      </Formik>
    </ContentLoader>
  );
};

export default NewAccountFormCollection;
